// Imports => React
import { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import { KEYS, THEMES, TYPES } from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcIsEmptyString } from "@opp/ui-kit";

// Imports => Hooks
import { useFormActions } from "@hooks";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcTextInput,
  AcSelectBox,
  AcButton,
  AcLoaderInline,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";

const _CLASSES = {
  MAIN: "ac-add-control-unit-type-modal  ac-modal--size-md",
  CONTENT: "ac-add-control-unit-type-modal__content",
};

const AcCreateTicketModal = ({
  store: { ui, tickets, profile },
  submit,
  t,
  i18n,
}) => {
  const history = useHistory();

  const [forceShowError, setForceShowError] = useState(false);

  const { current_merchant_profiles } = profile;

  const getInitialReference = useMemo(() => {
    const $traUID = document.querySelector('[id="ac-tra-uid"]');
    const $setUID = document.querySelector('[id="ac-set-uid"]');
    let result = "";

    if (AcIsSet($traUID)) {
      const value = $traUID.getAttribute("data-value");
      if (AcIsSet(value) && !AcIsEmptyString(value)) {
        result = value.trim();
      }
    } else if (AcIsSet($setUID)) {
      const value = $setUID.getAttribute("data-value");
      if (AcIsSet(value) && !AcIsEmptyString(value)) {
        result = value.trim();
      }
    }

    return result;
  }, [history]);

  const getCurrentMerchantEmailAddress = useMemo(() => {
    if (!AcIsSet(current_merchant_profiles)) {
      return "";
    }
    return current_merchant_profiles.email || "";
  }, [current_merchant_profiles]);

  const getCurrentMerchantName = useMemo(() => {
    if (!AcIsSet(current_merchant_profiles)) {
      return "";
    }
    return current_merchant_profiles.merchant.name || "";
  }, [current_merchant_profiles]);

  const [fields, setFields] = useState({
    customer_phonenumber: "",
    customer_emailaddress: getCurrentMerchantEmailAddress,
    customer_name: getCurrentMerchantName,
    subject: "",
    message: "",
    category: null,
    reference: getInitialReference || "",
    locale: i18n.resolvedLanguage,
  });

  const [errors, setErrors] = useState({
    customer_phonenumber: null,
    customer_emailaddress: AcIsEmptyString(getCurrentMerchantEmailAddress)
      ? undefined
      : null,
    customer_name: AcIsEmptyString(getCurrentMerchantName) ? undefined : null,
    subject: undefined,
    message: undefined,
    category: undefined,
    reference: null,
    locale: null,
  });
  const { hasErrors, handleInputChange, handleInputValidation } =
    useFormActions({
      t,
      fields,
      setFields,
      errors,
      setErrors,
    });

  const handleCancel = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    ui.reset(KEYS.MODAL, false);
  };

  const handleSubmit = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.persist) {
      event.persist();
    }

    if (hasErrors) {
      setForceShowError(true);
      return;
    }

    fields.locale = fields.locale.slice(0, 2);

    if (submit) {
      submit(t, fields).then((response) => {
        ui.setValue(KEYS.MODAL, KEYS.VISIBLE, false);
      });
    }
  };

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getCategorySelectOptions = useMemo(() => {
    const options = [
      // https://onlinepaymentplatform.atlassian.net/browse/MERAPI-249
      // {value: 'contact', name: `${t('category.contact', 'Contact', {ns: 'support'})}`},
      {
        value: "fraud",
        name: `${t("category.fraud", "Fraud", { ns: "support" })}`,
      },
      {
        value: "general",
        name: `${t("category.general", "General", { ns: "support" })}`,
      },
      {
        value: "payment",
        name: `${t("category.payment", "Payment", { ns: "support" })}`,
      },
    ];

    return {
      type: TYPES.TEXT,
      label: `${t("create-ticket-modal.category", "Category", {
        ns: "support",
      })}*`,
      name: "category",
      placeholder: `${t(
        "create-ticket-modal.choose-category",
        "Choose category",
        { ns: "support" }
      )}`,
      value: fields.category,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      options,
      forceShowError,
    };
  }, [fields, fields.category, forceShowError]);

  const getNameInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: `${t("create-ticket-modal.your-name", "Your name", {
        ns: "support",
      })}*`,
      name: "customer_name",
      value: fields.customer_name,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 1,
      focus: false,
      forceShowError,
    };
  }, [fields, fields.customer_name, forceShowError]);

  const getEmailAddressInputOptions = useMemo(() => {
    return {
      type: TYPES.EMAIL,
      label: `${t("create-ticket-modal.email-address", "Email address", {
        ns: "support",
      })}*`,
      name: "customer_emailaddress",
      value: fields.customer_emailaddress,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 2,
      focus: false,
      forceShowError,
    };
  }, [fields, fields.customer_emailaddress, forceShowError]);

  const getSubjectInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: `${t("create-ticket-modal.subject", "Subject", {
        ns: "support",
      })}*`,
      name: "subject",
      value: fields.subject,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 3,
      focus: true,
      minLength: 3,
      forceShowError,
    };
  }, [fields, fields.subject, forceShowError]);

  const getReferenceInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: `${t("create-ticket-modal.reference", "Reference", {
        ns: "support",
      })} ${t("create-ticket-modal.optional", "(optional)", {
        ns: "support",
      })}`,
      name: "reference",
      instructions: `${t(
        "create-ticket-modal.reference-instructions",
        "ie. transaction UID or payout UID",
        { ns: "support" }
      )}`,
      required: false,
      value: fields.reference,
      callback: handleInputChange,
      validation: (_, value) => (value.length > 20 ? "Max: 20" : null),
      tabIndex: 4,
      forceShowError,
    };
  }, [fields, fields.reference, forceShowError]);

  const getPhoneInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: `${t("create-ticket-modal.phone", "Phone number", {
        ns: "support",
      })} ${t("create-ticket-modal.optional", "(optional)", {
        ns: "support",
      })}`,
      name: "customer_phonenumber",
      required: false,
      value: fields.customer_phonenumber,
      callback: handleInputChange,
      validation: handleInputValidation,
      tabIndex: 5,
      forceShowError,
    };
  }, [fields, fields.customer_phonenumber, forceShowError]);

  const getMessageInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXTAREA,
      label: `${t(
        "create-ticket-modal.what-can-we-help-you-with",
        "What can we help you with?",
        { ns: "support" }
      )}*`,
      name: "message",
      value: fields.message,
      callback: handleInputChange,
      validation: handleInputValidation,
      tabIndex: 5,
      minLength: 3,
      forceShowError,
    };
  }, [fields, fields.message, forceShowError]);

  const getCancelButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.LIGHT,
      title: `${t("cancel", "Cancel", { ns: "common" })}`,
      callback: handleCancel,
    };
  }, [handleCancel]);

  const getSubmitButtonOptions = useMemo(() => {
    return {
      type: TYPES.SUBMIT,
      theme: THEMES.OMEGA,
      title: `${t("create-ticket", "Create ticket", { ns: "support" })}`,
      callback: handleSubmit,
    };
  }, [fields, errors, hasErrors, handleSubmit]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <form method={"post"} onSubmit={handleSubmit}>
          <AcContainer fluid>
            {tickets.is_busy ? (
              <AcLoaderInline loading={true} />
            ) : (
              <>
                <AcRow>
                  <AcColumn xs={12} sm={12} md={6}>
                    <AcTextInput {...getNameInputOptions} />
                  </AcColumn>

                  <AcColumn xs={12} sm={12} md={6}>
                    <AcTextInput {...getEmailAddressInputOptions} />
                  </AcColumn>
                </AcRow>

                <AcRow>
                  <AcColumn xs={12} sm={12} md={6}>
                    <AcTextInput {...getReferenceInputOptions} />
                  </AcColumn>

                  <AcColumn xs={12} sm={12} md={6}>
                    <AcTextInput {...getPhoneInputOptions} />
                  </AcColumn>
                </AcRow>

                <AcRow>
                  <AcColumn>
                    <AcSelectBox {...getCategorySelectOptions} />
                  </AcColumn>
                </AcRow>

                <AcRow>
                  <AcColumn>
                    <AcTextInput {...getSubjectInputOptions} />
                  </AcColumn>
                </AcRow>

                <AcRow>
                  <AcColumn>
                    <AcTextInput {...getMessageInputOptions} />
                  </AcColumn>
                </AcRow>

                <AcRow className={"h-margin-top-20 h-padding-bottom-10"}>
                  <AcColumn
                    xs={5}
                    sm={6}
                    className={"h-text--align-left h-flex-v-align-center"}
                  >
                    <AcButton {...getCancelButtonOptions}>
                      <span>{t("cancel", "Cancel", { ns: "common" })}</span>
                    </AcButton>
                  </AcColumn>
                  <AcColumn xs={7} sm={6} className={"h-text--align-right"}>
                    <AcButton {...getSubmitButtonOptions}>
                      <span>
                        {t("create-ticket", "Create ticket", { ns: "support" })}
                      </span>
                    </AcButton>
                  </AcColumn>
                </AcRow>
              </>
            )}
          </AcContainer>
        </form>
      </div>
    </div>
  );
};

export default withTranslation(["support", "common", "store"])(
  withStore(observer(AcCreateTicketModal))
);
